// createAesEmbeddedManifestDataUrl.js
'use strict';

import { captureException } from '@sentry/browser';

import fetch from '../resource/customFetch.js';
import { player as playerDebug } from '../resource/debug.js';
import { getHeaders } from '../resource/fetchOptionHeader.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import { getPlaybackLogPrefix } from '../resource/playerLogUtils.js';
import { unwrapAesKey } from '../resource/responseDecryptionUtil.js';

const playbackBaseLog = playerDebug.extend('log:playback');
const playbackBaseError = playerDebug.extend('error:playback');

const uint8ToBase64 = bytes => {
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

const ABSOLUTE_URI_SCHEME_REGEX = /^[a-z][a-z0-9+.-]*:/i;
const absolutizeUri = (uri, baseUrl) => {
  if (!uri || ABSOLUTE_URI_SCHEME_REGEX.test(uri)) return uri;
  try {
    return new URL(uri, baseUrl).href;
  } catch (error) {
    return uri;
  }
};

const CODECS_ATTRIBUTE_REGEX = /(?:^|[:,])\s*CODECS="([^"]+)"/;

const getCodecsFromStreamInfoLine = line => {
  const codecsMatch = line.match(CODECS_ATTRIBUTE_REGEX);
  return codecsMatch?.[1] || null;
};

/**
 * Pick the first variant URI and its codecs from an HLS master playlist.
 * @param {string} manifestText - Raw M3U8 master playlist text.
 * @returns {{ uri: string, codecs: string|null }}
 * @throws {Error} If the playlist contains no variant URI.
 */
const pickFirstVariant = manifestText => {
  let codecs = null;

  for (const line of manifestText.split(/\r?\n/)) {
    const trimmedLine = line.trim();
    if (!trimmedLine) continue;

    if (trimmedLine.startsWith('#EXT-X-STREAM-INF')) {
      codecs = getCodecsFromStreamInfoLine(trimmedLine);
      continue;
    }

    if (!trimmedLine.startsWith('#')) {
      return { uri: trimmedLine, codecs };
    }
  }

  throw new Error('aes_embedded_manifest_master_without_variant');
};

// Key endpoint returns { nonce, wrapped_key }, not raw bytes.
const fetchAndUnwrapAesKey = async ({
  keyUrl,
  authHeaders,
  clientId,
  sessionId,
  messageId,
  metadata,
}) => {
  let rewrittenKeyUrl = keyUrl;
  try {
    const urlObj = new URL(keyUrl);
    urlObj.host = new URL(getResourceUrl({ endpoint: '/' }).href).host;
    urlObj.pathname = urlObj.pathname.replace(
      /\/messages\/[^/]+\//,
      `/messages/${messageId}/`
    );
    rewrittenKeyUrl = urlObj.href;
  } catch (error) {
    // fall back to original keyUrl
  }

  let response;
  try {
    response = await fetch(rewrittenKeyUrl, {
      method: 'GET',
      headers: authHeaders,
    });
  } catch (error) {
    error.stage = 'key.fetch';
    throw error;
  }
  if (!response.ok) {
    const error = new Error(
      `aes_embedded_manifest_key_error [${response.status}] ${rewrittenKeyUrl}`
    );
    error.stage = 'key.fetch';
    throw error;
  }

  try {
    const { nonce, wrapped_key: wrappedKey } = await response.json();
    const rawKey = await unwrapAesKey({
      clientId,
      sessionId,
      nonce,
      wrappedKey,
    });
    if (metadata) metadata.keyByteLength = rawKey?.byteLength;
    return rawKey;
  } catch (error) {
    const wrapped = new Error(
      `aes_embedded_manifest_key_error: ${error.message} ${rewrittenKeyUrl}`,
      { cause: error }
    );
    wrapped.stage = 'key.unwrap';
    throw wrapped;
  }
};

const rewriteMediaPlaylist = async ({
  manifestText,
  manifestUrl,
  authHeaders,
  clientId,
  sessionId,
  messageId,
  metadata,
}) => {
  const inlineAesKeyLine = async line => {
    const uriMatch = line.match(/URI="([^"]+)"/);
    if (!uriMatch) return line;
    const [, keyUrl] = uriMatch;
    if (keyUrl.startsWith('data:')) return line;

    const rawKey = await fetchAndUnwrapAesKey({
      keyUrl,
      authHeaders,
      clientId,
      sessionId,
      messageId,
      metadata,
    });
    const inlined = `data:application/octet-stream;base64,${uint8ToBase64(new Uint8Array(rawKey))}`;
    return line.replace(/URI="[^"]*"/, `URI="${inlined}"`);
  };

  const absolutizeUriAttrs = line =>
    line.replace(
      /URI="([^"]+)"/g,
      (_, uri) => `URI="${absolutizeUri(uri, manifestUrl)}"`
    );

  const rewriteLine = line => {
    if (line.startsWith('#EXT-X-KEY')) return inlineAesKeyLine(line);
    if (line.startsWith('#')) return absolutizeUriAttrs(line);
    if (line.trim() === '') return line;
    return absolutizeUri(line.trim(), manifestUrl);
  };

  const rewritten = await Promise.all(
    manifestText.split(/\r?\n/).map(rewriteLine)
  );
  return rewritten.join('\n');
};

// `#EXT-X-STREAM-INF` is a master-playlist-only tag; presence means it is a master, not a media playlist.
const MASTER_PLAYLIST_REGEX = /^#EXT-X-STREAM-INF/m;

/**
 * Fetch an HLS media playlist, embed the AES-128 key, and keep master CODECS.
 *
 * @param {object} parameters
 * @param {string} parameters.manifestUrl - Original HLS manifest URL.
 * @param {string} parameters.clientId
 * @param {string} parameters.sessionId
 * @param {string} parameters.token
 * @param {string} parameters.messageId
 * @returns {Promise<{ dataUrl: string, codecs: string | null }>} Rewritten manifest data URL and selected variant codecs.
 * @example
 * createAesEmbeddedManifestSource({ manifestUrl, clientId, sessionId, token, messageId });
 * // => { dataUrl: 'data:application/vnd.apple.mpegurl;base64,...', codecs: 'avc1.64001f' }
 */
const createAesEmbeddedManifestSource = async ({
  manifestUrl,
  clientId,
  sessionId,
  token,
  messageId,
}) => {
  const startedAt = Date.now();
  const metadata = {};
  const prefix = getPlaybackLogPrefix({ messageId });
  const authHeaders = {
    ...getHeaders(),
    ...(clientId && { 'X-Client-ID': clientId }),
    ...(sessionId && { 'X-Session-ID': sessionId }),
    ...(token && { Authorization: `Bearer ${token}` }),
  };

  try {
    const manifestResponse = await fetch(manifestUrl, { method: 'GET' });
    if (!manifestResponse.ok) {
      throw new Error(
        `aes_embedded_manifest_fetch_error [${manifestResponse.status}] ${manifestUrl}`
      );
    }
    let manifestText = await manifestResponse.text();
    let resolvedManifestUrl = manifestUrl;

    // Collapse master to first variant: nested data URLs are flaky in
    // Safari, and backend current ships one quality per asset (trace from .m3u8).
    if (MASTER_PLAYLIST_REGEX.test(manifestText)) {
      const variant = pickFirstVariant(manifestText);
      metadata.variantName = variant.uri;
      metadata.hlsCodecs = variant.codecs;
      resolvedManifestUrl = new URL(variant.uri, manifestUrl).href;

      const variantResponse = await fetch(resolvedManifestUrl, {
        method: 'GET',
      });
      if (!variantResponse.ok) {
        throw new Error(
          `aes_embedded_manifest_fetch_error [${variantResponse.status}] ${resolvedManifestUrl}`
        );
      }
      manifestText = await variantResponse.text();
    }

    const rewritten = await rewriteMediaPlaylist({
      manifestText,
      manifestUrl: resolvedManifestUrl,
      authHeaders,
      clientId,
      sessionId,
      messageId,
      metadata,
    });
    const base64 = uint8ToBase64(new TextEncoder().encode(rewritten));
    const dataUrl = `data:application/vnd.apple.mpegurl;base64,${base64}`;

    playbackBaseLog(`${prefix} source.ready`, {
      messageId,
      sourceType: 'aes-hls',
      sourcePreparationTimeMs: Date.now() - startedAt,
      variantName: metadata.variantName || null,
      hlsCodecs: metadata.hlsCodecs || null,
      keyByteLength: metadata.keyByteLength || null,
    });

    return { dataUrl, codecs: metadata.hlsCodecs || null };
  } catch (error) {
    const stage =
      error.stage ||
      (() => {
        const message = error?.message || '';
        if (message.includes('fetch_error')) return 'manifest.fetch';
        if (message.includes('without_variant')) return 'variant.resolve';
        return 'manifest.rewrite';
      })();

    playbackBaseError(`${prefix} source.failed`, {
      messageId,
      sourceType: 'aes-hls',
      stage,
      errorMessage: error.message,
      sourcePreparationTimeMs: Date.now() - startedAt,
    });

    captureException(error, {
      tags: { scope: 'createAesEmbeddedManifestDataUrl' },
      extra: { manifestUrl },
    });
    throw new Error(`aes_embedded_manifest_failed: ${error.message}`, {
      cause: error,
    });
  }
};

/**
 * Fetch an HLS media playlist and embed the AES-128 key as a data: URI.
 *
 * @param {object} parameters
 * @param {string} parameters.manifestUrl - Original HLS manifest URL.
 * @param {string} parameters.clientId
 * @param {string} parameters.sessionId
 * @param {string} parameters.token
 * @param {string} parameters.messageId
 * @returns {Promise<string>} Data URL of the rewritten manifest.
 * @example
 * createAesEmbeddedManifestDataUrl({ manifestUrl, clientId, sessionId, token, messageId });
 * // => 'data:application/vnd.apple.mpegurl;base64,...'
 */
const createAesEmbeddedManifestDataUrl = async parameters => {
  const { dataUrl } = await createAesEmbeddedManifestSource(parameters);
  return dataUrl;
};

export { createAesEmbeddedManifestSource };
export default createAesEmbeddedManifestDataUrl;
